/**
 * SidebarItem Component
 *
 * Individual navigation item with icon and label.
 * Features active indicator (black semi-circle on left edge).
 */

import { cn } from '@/lib/utils'

export default function SidebarItem({
  label,
  icon: Icon,
  isExpanded,
  isActive,
  onClick,
  isNested = false,
  disabled = false,
  variant = 'default',
  badge: Badge = null
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'group relative w-full flex items-center gap-3 py-2 rounded-md',
        'transition-colors duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isExpanded ? 'px-3 justify-start' : 'justify-center px-0',
        isNested && isExpanded && 'pl-6',
        // States
        !disabled && 'hover:bg-accent/50',
        disabled && 'opacity-50 cursor-default',
        isActive && 'bg-accent text-foreground font-medium',
        // Variants
        variant === 'destructive' && !disabled && 'hover:bg-destructive/10 hover:text-destructive'
      )}
      aria-current={isActive ? 'page' : undefined}
      aria-label={label}
    >
      {/* Active Indicator - black semi-circle on left edge */}
      {isActive && (
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-6 bg-foreground rounded-r-full"
          aria-hidden="true"
        />
      )}

      <Icon
        className={cn(
          'w-5 h-5 flex-shrink-0',
          isActive ? 'text-foreground' : 'text-muted-foreground',
          variant === 'destructive' && 'group-hover:text-destructive',
          isNested && 'w-4 h-4'
        )}
      />

      {isExpanded && (
        <span
          className={cn(
            'text-sm truncate',
            isNested && 'text-sm',
            variant === 'destructive' && 'group-hover:text-destructive'
          )}
        >
          {label}
        </span>
      )}

      {/* Badge in expanded state - rendered as separate flex child */}
      {isExpanded && Badge && <Badge className="ml-auto" />}

      {/* Badge in collapsed state */}
      {!isExpanded && Badge && (
        <div className="absolute -top-1 -right-1">
          <Badge />
        </div>
      )}

      {/* Tooltip for collapsed state */}
      {!isExpanded && (
        <div
          className={cn(
            'absolute left-full top-1/2 -translate-y-1/2 ml-2',
            'px-2 py-1 rounded bg-popover border border-border shadow-md',
            'text-sm text-popover-foreground whitespace-nowrap',
            'opacity-0 pointer-events-none',
            'group-hover:opacity-100 group-hover:pointer-events-auto',
            'transition-opacity duration-150 z-50'
          )}
          role="tooltip"
        >
          {label}
        </div>
      )}
    </button>
  )
}
